import { AST, ColumnRef, ExpressionValue, Parser } from 'node-sql-parser';
import { isBinaryExpr, isOperator } from '../../../utils/filter/SqlToFilterConverter/SqlToFilterConverter.utils';

const parser = new Parser();

const isStringLiteral = (n: ExpressionValue): n is { type: 'double_quote_string' | 'single_quote_string' | 'string'; value: string } =>
  n && typeof n === 'object' && ['double_quote_string', 'single_quote_string', 'string'].includes(n.type);

const isNullLiteral = (n: ExpressionValue): boolean => n?.type === 'null' || (isStringLiteral(n) && n.value?.toLowerCase() === 'null');

const toColumnRefIfQuoted = (n: ExpressionValue): ColumnRef | ExpressionValue =>
  n?.type === 'double_quote_string' ? { type: 'column_ref', table: null, column: n.value } : n;

const addWildcards = (n: ExpressionValue): ExpressionValue => {
  if (!isStringLiteral(n)) {
    return n;
  }
  const value = n.value ?? '';
  return value.includes('%') || value.includes('_') ? n : { ...n, value: `%${value}%` };
};

const processNullComparison = (op: string) => ({
  processedRight: { type: 'null', value: null },
  finalOperator: op === '=' ? 'IS' : op === '!=' ? 'IS NOT' : op,
});

const processLikeComparison = (right: ExpressionValue, operator: string) => ({
  processedRight: addWildcards(right),
  finalOperator: operator,
});

const processRegularComparison = (right: ExpressionValue, operator: string) => ({
  processedRight: right,
  finalOperator: operator,
});

function rewriteWhere(node: ExpressionValue): ExpressionValue {
  if (!node || typeof node !== 'object' || !isBinaryExpr(node)) {
    return node;
  }

  const rewrittenLeft = rewriteWhere(node.left);
  const rewrittenRight = rewriteWhere(node.right);
  const op = String(node.operator || '').toUpperCase();

  if (!isOperator(op)) {
    return rewrittenLeft === node.left && rewrittenRight === node.right ? node : { ...node, left: rewrittenLeft, right: rewrittenRight };
  }

  const processedLeft = toColumnRefIfQuoted(rewrittenLeft);
  const isNull = isNullLiteral(rewrittenLeft) || isNullLiteral(rewrittenRight);
  const isLike = (op === 'LIKE' || op === 'NOT LIKE') && isStringLiteral(rewrittenRight);

  const { processedRight, finalOperator } = isNull
    ? processNullComparison(op)
    : isLike
      ? processLikeComparison(rewrittenRight, node.operator)
      : processRegularComparison(rewrittenRight, node.operator);

  const hasChanges = processedLeft !== rewrittenLeft || processedRight !== rewrittenRight || finalOperator !== node.operator;
  return hasChanges ? { ...node, left: processedLeft, right: processedRight, operator: finalOperator } : node;
}

export function transformLegacySQLDialect(sql: string): string {
  const isSelect = /^\s*select\b/i.test(sql);
  const wrapped = isSelect ? sql : `SELECT * FROM logs WHERE ${sql}`;
  const ast = parser.astify(wrapped, { database: 'mysql' });
  const stmt = Array.isArray(ast) ? ast[0] : ast;

  const rewrittenWhere = 'where' in stmt && stmt.where ? rewriteWhere(stmt.where) : null;
  const transformedStmt = 'where' in stmt && stmt.where && rewrittenWhere !== stmt.where ? { ...stmt, where: rewrittenWhere } : stmt;

  const normalized = parser.sqlify(transformedStmt as AST, { database: 'mysql' });

  if (isSelect) {
    return normalized;
  }

  const match = normalized.match(/\bWHERE\b\s+([\s\S]*)$/i);
  return (match?.[1] || normalized).trim();
}
